import { ref } from 'vue'
import { useNotification } from '@kyvg/vue3-notification'

/**
 * Optimistic moderation, held as an overlay on top of room state rather than
 * as a mutation of it.
 *
 * Every moderation action is a proxied round trip — dashboard to Castr core to
 * the room worker — and its result comes back not in the response body but as
 * a broadcast frame arriving later on the WebSocket. Waiting for that whole
 * path with nothing moving on screen is what makes a delete feel like a freeze.
 *
 * So the effect is recorded here on click and the components read room state
 * *through* this overlay, while `useAudienceRoom` keeps writing only what the
 * socket actually said. Keeping the two apart is what makes the awkward cases
 * fall out for free rather than needing to be handled:
 *
 * - **Reconciliation is idempotent.** The broadcast removes the row from the
 *   source list on its own; the overlay entry then merely describes something
 *   already true, and `settle` drops it. There is no second removal to guard
 *   against and nothing to flicker.
 * - **A rollback restores a row exactly where it was**, because it was never
 *   removed from the ordered list it lives in — only hidden by a filter.
 * - **`hello` on reconnect, the purge race, the generation counter** — none of
 *   them have to learn that optimism exists.
 *
 * Entries survive until `settle` sees the server agree, so an action whose
 * broadcast is slow stays applied instead of blinking back and forth.
 */
export function useOptimisticActions() {
	const { notify } = useNotification()

	// Replaced rather than mutated on write: a Map is not deeply reactive, and
	// the whole point is that a computed reading it re-runs.
	const pending = ref(new Map())

	const has = (key) => pending.value.has(key)

	/** The recorded effect, or `fallback` when nothing is pending for `key`. */
	const get = (key, fallback = null) => (pending.value.has(key) ? pending.value.get(key) : fallback)

	const write = (mutate) => {
		const next = new Map(pending.value)
		mutate(next)
		pending.value = next
	}

	const forget = (key) => write((map) => map.delete(key))

	/**
	 * Records `effect` under `key`, sends the request, and takes the effect
	 * back if it fails.
	 *
	 * A failed action has to be *seen* to fail. A row that quietly reappears
	 * reads as a UI glitch, and the moderator walks away believing they deleted
	 * something they did not, so the rollback always speaks.
	 *
	 * @param {object} action
	 * @param {string} action.key Identifies the thing being acted on. A second
	 *   click while the first is in flight is ignored.
	 * @param {*} [action.effect] What the UI should read until the server
	 *   agrees. Defaults to `true`, which suits a plain hide-this-row.
	 * @param {() => Promise<any>} action.request
	 * @param {string} action.failure Sentence shown if the request is refused.
	 * @returns {Promise<boolean>} Whether it went through.
	 */
	const run = async ({ key, effect = true, request, failure }) => {
		if (has(key)) return false

		write((map) => map.set(key, effect))

		try {
			await request()
			return true
		} catch (err) {
			forget(key)
			notify({ group: 'error', text: err.message ? `${failure} ${err.message}` : failure })
			return false
		}
	}

	/**
	 * Drops every entry the server has caught up with.
	 *
	 * Call it from a watcher on the room state the overlay covers. `confirmed`
	 * decides, per entry, whether the room now says the same thing the overlay
	 * has been saying.
	 *
	 * @param {(key: string, effect: any) => boolean} confirmed
	 */
	const settle = (confirmed) => {
		const stale = []
		pending.value.forEach((effect, key) => {
			if (confirmed(key, effect)) stale.push(key)
		})
		if (!stale.length) return

		write((map) => stale.forEach((key) => map.delete(key)))
	}

	return { pending, has, get, run, forget, settle }
}
